import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Paper, Typography, Tabs, Tab, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid2';
import axios from 'axios';
import DataLoader from '../components/common/DataLoader.js';
import config from '../config.js';
import { formatEuro, formatPct } from '../functions/formatNumber.js';
import HistoricInversioChart from '../components/dashboard-inversio/HistoricInversioChart.js';
import { ActiuCardGlobal, AnyCardGlobal } from '../components/dashboard-inversio/cards/index.js';

const DashboardInversioGlobal = () => {

  const { userapp } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tabValue, setTabValue] = useState(0);
  const [selectedAssetId, setSelectedAssetId] = useState(null);

  const fetchFunction = (jwtToken, onData, onError, onFinally) => {
    axios.get(`${config.apiBaseUrl}/v1/resumglobalinversio/${userapp}`, {
      headers: { Authorization: `${jwtToken}` }
    })
    .then((response) => {
      onData(response);
    })
    .catch((err) => {
      onError(err, 'carregar dades de inversió global');
    })
    .finally(() => onFinally());
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleRowClick = (assetId) => {
    setSelectedAssetId(prev => prev === assetId ? null : assetId);
  };

  const formatAportatRetirat = (aportat, retirat) => {
    const apStr = aportat > 0 ? `+${formatEuro(aportat)}` : '0,00 €';
    const retStr = retirat > 0 ? `-${formatEuro(retirat)}` : '0,00 €';
    return `${apStr} / ${retStr}`;
  };

  return (
    <DataLoader id={'global_inversio'} fetchFunction={fetchFunction}>
      {(data) => {
        const assets = data.actius || [];
        const years = data.anys || [];

        const totalGuanyPerdua = data.totalGuanyPerdua || 0;
        const colorGP = totalGuanyPerdua >= 0 ? '#4CAF50' : '#EF4444';
        const colorGPBg = totalGuanyPerdua >= 0 ? 'rgba(76, 175, 80, 0.12)' : 'rgba(239, 68, 68, 0.12)';

        const tir = data.percentatgeRetornAnualitzat !== undefined && data.percentatgeRetornAnualitzat !== null
          ? data.percentatgeRetornAnualitzat
          : 0;
        const tirColor = tir >= 0 ? '#2E7D32' : '#D32F2F';
        const tirBg = tir >= 0 ? 'rgba(76, 175, 80, 0.12)' : 'rgba(239, 68, 68, 0.12)';
        const tirBorder = tir >= 0 ? '#A5D6A7' : '#FFCDD2';

        return (
          <>
            <Grid size={12} sx={{ marginTop: '10px' }}>
              <Typography variant="h5">
                Dashboard Inversió Global
              </Typography>
            </Grid>

            {/* Tarjetas resumen superior y contenido envuelto en un único Grid container sin width: 100% */}
            <Grid container spacing={3} sx={{ marginTop: '10px' }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper sx={{ padding: '12px', textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary">Valor Actual Cartera</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#3F51B5', mt: 0.5 }}>
                    {formatEuro(data.totalValorActual || 0)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper sx={{ padding: '12px', textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary">Capital Invertit</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#666666', mt: 0.5 }}>
                    {formatEuro(data.totalInvertit || 0)}
                  </Typography>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper sx={{ padding: '12px', textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary">Guany / Pèrdua</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', mt: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: colorGP }}>
                      {totalGuanyPerdua >= 0 ? '+' : ''}{formatEuro(totalGuanyPerdua)}
                    </Typography>
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: colorGPBg,
                      color: colorGP,
                      border: `1px solid ${totalGuanyPerdua >= 0 ? '#A5D6A7' : '#FFCDD2'}`,
                      borderRadius: '12px',
                      px: 1,
                      py: 0.2,
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                    }}>
                      {data.percentatgeRetorn >= 0 ? '+' : ''}{formatPct(data.percentatgeRetorn || 0)}
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper sx={{ padding: '12px', textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary">Rentabilitat Anualitzada (TIR)</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.8 }}>
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: tirBg,
                      color: tirColor,
                      border: `1px solid ${tirBorder}`,
                      borderRadius: '16px',
                      px: 1.5,
                      py: 0.4,
                      fontSize: '1.15rem',
                      fontWeight: 'bold',
                    }}>
                      {tir >= 0 ? '+' : ''}{formatPct(tir)} a.a.
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Gráfico de evolución */}
              <Grid size={12} sx={{ mt: 1 }}>
                <HistoricInversioChart 
                  assets={assets} 
                  isDaily={false} 
                  selectedAssetId={selectedAssetId} 
                />
              </Grid>

              {/* Pestañas de detalle */}
              <Grid size={12} sx={{ mt: 1 }}>
                <Paper>
                <Tabs value={tabValue} onChange={handleTabChange} indicatorColor="primary" textColor="primary" centered>
                  <Tab label="Actius" />
                  <Tab label="Anys" />
                </Tabs>

                {/* Pestanya 1: Actius */}
                {tabValue === 0 && (
                  <Box sx={{ p: isMobile ? 1 : 2 }}>
                    {isMobile ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {assets.map((row) => (
                          <ActiuCardGlobal
                            key={row.actiuId}
                            asset={row}
                            isSelected={selectedAssetId === row.actiuId}
                            onClick={() => handleRowClick(row.actiuId)}
                          />
                        ))}
                        {assets.length === 0 && (
                          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                            No hi ha actius registrats
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Nom actiu</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Quantitat</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Últim preu</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Valor actual</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total invertit</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Guany / Pèrdua</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Retorn</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>TIR (a.a.)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {assets.map((row) => {
                              const isSelected = selectedAssetId === row.actiuId;
                              const gpColor = row.guanyPerdua >= 0 ? '#4CAF50' : '#EF4444';
                              const tirVal = row.percentatgeRetornAnualitzat !== undefined && row.percentatgeRetornAnualitzat !== null
                                ? row.percentatgeRetornAnualitzat
                                : null;
                              const tirValColor = (tirVal || 0) >= 0 ? '#2E7D32' : '#D32F2F';
                              return (
                                <TableRow
                                  key={row.actiuId}
                                  hover
                                  onClick={() => handleRowClick(row.actiuId)}
                                  sx={{
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? 'rgba(63, 81, 181, 0.08)' : 'inherit',
                                    '&:hover': {
                                      backgroundColor: isSelected ? 'rgba(63, 81, 181, 0.12) !important' : 'rgba(0, 0, 0, 0.04)',
                                    }
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: isSelected ? 'bold' : 'normal' }}>
                                    {row.nom}
                                  </TableCell>
                                  <TableCell align="right">{row.quantitat}</TableCell>
                                  <TableCell align="right">{formatEuro(row.darrerPreu)}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatEuro(row.valorActual)}</TableCell>
                                  <TableCell align="right">{formatEuro(row.totalInvertit)}</TableCell>
                                  <TableCell align="right" sx={{ color: gpColor, fontWeight: 'bold' }}>
                                    {row.guanyPerdua >= 0 ? '+' : ''}{formatEuro(row.guanyPerdua)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: gpColor, fontWeight: 'bold' }}>
                                    {row.percentatgeRetorn >= 0 ? '+' : ''}{formatPct(row.percentatgeRetorn)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: tirValColor, fontWeight: 'bold' }}>
                                    {tirVal !== null ? `${tirVal >= 0 ? '+' : ''}${formatPct(tirVal)} a.a.` : '-'}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}

                {/* Pestanya 2: Anys */}
                {tabValue === 1 && (
                  <Box sx={{ p: isMobile ? 1 : 2 }}>
                    {isMobile ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {years.map((row) => (
                          <AnyCardGlobal
                            key={row.anio}
                            year={row}
                            onClick={() => navigate(`/${userapp}/inversio/${row.anio}`)}
                          />
                        ))}
                        {years.length === 0 && (
                          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                            No hi ha dades anuals registrades
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Any</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Saldo Final</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Aportat / Retirat</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Guany / Pèrdua</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rendiment</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Rentabilitat (%)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {years.map((row) => {
                              const gp = (row.saldoFinal || 0) - (row.saldoInitial || 0);
                              const gpColor = gp >= 0 ? '#4CAF50' : '#EF4444';
                              const rendColor = (row.rendimientoTotal || 0) >= 0 ? '#4CAF50' : '#EF4444';
                              const retVal = row.percentatgeRetorn !== undefined && row.percentatgeRetorn !== null
                               ? row.percentatgeRetorn
                                : null;
                              const retColor = (retVal || 0) >= 0 ? '#4CAF50' : '#EF4444';
                              return (
                                <TableRow
                                  key={row.anio}
                                  hover
                                  onClick={() => navigate(`/${userapp}/inversio/${row.anio}`)}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  <TableCell sx={{ color: '#3F51B5', fontWeight: 'bold' }}>
                                    {row.anio}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatEuro(row.saldoFinal)}</TableCell>
                                  <TableCell align="center">
                                    {formatAportatRetirat(row.aportacions, row.retiros)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: gpColor, fontWeight: 'bold' }}>
                                    {gp >= 0 ? '+' : ''}{formatEuro(gp)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: rendColor, fontWeight: 'bold' }}>
                                    {(row.rendimientoTotal || 0) >= 0 ? '+' : ''}{formatEuro(row.rendimientoTotal)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: retColor, fontWeight: 'bold' }}>
                                    {retVal !== null ? `${retVal >= 0 ? '+' : ''}${formatPct(retVal)}` : '-'}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      );
    }}
    </DataLoader>
  );
};

export default DashboardInversioGlobal;
